import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Table,
  Upload,
} from "antd";
import axios from "axios";
import moment from "moment";
import { useCallback, useState, useEffect } from "react";
import { MdFileDownload, MdUpload } from "react-icons/md";
import * as XLSX from "xlsx";
import { API } from "../../../Config";
import File from "../../Delivery/excel/PURCHASE_EXCELL_FILE.xlsx";
import { useSelector } from "react-redux";

const PurchaseForm = (props) => {
  const Auth = useSelector((state) => state.Auth.user);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [modalWidth, setModalWidth] = useState(1200);
  const dateFormat = "DD/MM/YYYY";
  const selectedPurchase = props?.selectedPurchase || "";

  useEffect(() => {
    const updateModalWidth = () => {
      if (window.innerWidth > 1400) {
        setModalWidth(1200);
      } else if (window.innerWidth > 992) {
        setModalWidth(1100);
      } else if (window.innerWidth > 768) {
        setModalWidth('85%');
      } else {
        setModalWidth('90%');
      }
    };

    updateModalWidth();
    window.addEventListener('resize', updateModalWidth);
    return () => window.removeEventListener('resize', updateModalWidth);
  }, []);

  const getInitialDataSource = () => {
    if (props?.selectedPurchase?.purchaseDetails?.length > 0) {
      return props?.selectedPurchase?.purchaseDetails?.map((item) => ({
        ...item,
        key: item.id || Date.now() + Math.random(),
        discount: item.discount || 0,
      }));
    } else {
      return [
        {
          key: Date.now(),
          name: "",
          price: 1,
          description: "",
          discount: 0,
          quantity: 1,
        },
      ];
    }
  };

  const [dataSource, setDataSource] = useState(getInitialDataSource());

  const handleFinish = async (values) => {
    try {
      setLoading(true);
      const emptyNameRow = dataSource.find(
        (item) => !item.name || item.name.trim() === ""
      );
      if (emptyNameRow) {
        message.error("Please enter item name for all products.");
        setLoading(false);
        return;
      }
      const invalidRow = dataSource.find(
        (item) => !item.quantity || Number(item.quantity) <= 0
      );
      if (invalidRow) {
        message.error("Please enter valid quantity for all products.");
        setLoading(false);
        return;
      }

      const payload = {
        description: values.description,
        supplierId: values.supplierId,
        userid: Number(Auth?.id),
        payment_date: values.purchase_date,
        items: dataSource.map((item) => ({
          name: item?.name?.toString(),
          price: Number(item.price),
          discount: Number(item.discount) || 0,
          quantity: Number(item.quantity),
          description: item.description || "",
          key: Number(item.id),
        })),
      };

      const response = selectedPurchase?.id
        ? await axios.put(
            `${API.BASR_URL}${API.PURCHASE}/${selectedPurchase.id}`,
            payload
          )
        : await axios.post(`${API.BASR_URL}${API.PURCHASE}`, payload);
      if (response?.status) {
        message.success(
          `Purchase ${selectedPurchase?.id ? "updated" : "added"}  successfully`
        );
        resetFormAndData();
        props?.onClose();
        props?.relaod();
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      const errorMessage = error?.response?.data?.message || error?.message || 'Something went wrong';
      message.error(errorMessage);
    }
  };

  const renderSummary = () => {
    const totalQuantity = dataSource?.reduce(
      (acc, item) => acc + Number(item.quantity || 0),
      0
    );
    const totalPrice = dataSource?.reduce(
      (acc, item) =>
        acc +
        (Number(item.price) * Number(item.quantity) -
          Number(item.discount || 0)),
      0
    );
    return (
      <Table.Summary.Row style={{ height: 50 }}>
        <Table.Summary.Cell index={0} />
        <Table.Summary.Cell index={1}>
          <Button onClick={handleAddProduct}>Add New</Button>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={2} />
        <Table.Summary.Cell index={3}>
          <strong>Total Quantity</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={4}>
          <strong>{totalQuantity || 0}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={5}>
          <strong>Total Price</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={5}>
          <strong>{Number(totalPrice || 0).toFixed(2)}</strong>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };
  const handleChangeValueSet = useCallback((value, key, field) => {
    setDataSource((prev) => {
      const newDataSource = prev.map((val) => {
        if (val.key === key) {
          const updates = { [field]: value };

          // Validation: discount should not exceed price
          if (field === "discount") {
            const numericPrice = Number(val.price || 0);
            const numericDiscount = Number(value || 0);
            if (numericDiscount > numericPrice) {
              Modal.warning({
                title: "Invalid Discount",
                content: "Discount cannot be greater than the price.",
              });
              return val; // don't update
            }
          }

          return { ...val, ...updates };
        }
        return val;
      });

      return newDataSource;
    });
  }, []);

  const handleAddProduct = useCallback(() => {
    setDataSource((prev) => [
      ...prev,
      {
        key: Date.now(), // Unique key
        name: "",
        price: 0,
        discount: 0,
        quantity: 1,
      },
    ]);
  }, []);

  const handleExcelUpload = (file) => {
    setUploading(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        const transformedData = jsonData.map((row, index) => {
          const normalizedRow = Object.entries(row || {}).reduce(
            (acc, [key, value]) => {
              if (typeof key === "string" && key.trim()) {
                acc[key.trim().toLowerCase()] = value;
              }
              return acc;
            },
            {}
          );

          const getString = (...keys) => {
            for (const key of keys) {
              const value = normalizedRow[key];
              if (
                value !== undefined &&
                value !== null &&
                String(value).trim() !== ""
              ) {
                return String(value).trim();
              }
            }
            return "";
          };

          const getNumber = (fallback, ...keys) => {
            for (const key of keys) {
              let value = normalizedRow[key];
              if (value !== undefined && value !== null && `${value}`.trim() !== "") {
                if (typeof value === "string") {
                  value = value.replace(/,/g, "").trim();
                }
                const parsed = Number(value);
                if (Number.isFinite(parsed)) {
                  return parsed;
                }
              }
            }
            return fallback;
          };

          const name = getString("name", "item name", "product", "item");
          const description = getString("description", "desc", "details");
          const price = getNumber(0, "price", "rate", "amount", "unit price");
          const quantity = getNumber(1, "quantity", "qty", "qnty", "units");
          const discount = getNumber(0, "discount", "discount amount", "disc");

          return {
            key: Date.now() + index,
            name,
            description,
            price,
            discount,
            quantity,
          };
        });

        const hasRows = transformedData.length > 0;
        const isValidData =
          hasRows &&
          transformedData.every(
            (item) =>
              item.name &&
              Number.isFinite(item.price) &&
              Number.isFinite(item.discount) &&
              Number.isFinite(item.quantity) &&
              item.price >= 0 &&
              item.discount >= 0 &&
              item.quantity > 0
          );

        if (!hasRows) {
          message.warning("The uploaded file is empty.");
        } else if (isValidData) {
          setDataSource(
            transformedData.map((item) => ({
              ...item,
              description: item.description || "",
            }))
          );
          message.success(
            `Successfully imported ${transformedData.length} products from Excel`
          );
        } else {
          message.error(
            "Invalid data format in Excel file. Please ensure required columns contain valid values."
          );
        }
      } catch (error) {
        message.error(
          "Error reading Excel file. Please ensure it's a valid Excel file."
        );
        console.error("Excel upload error:", error);
      } finally {
        setUploading(false);
      }
    };

    reader.readAsArrayBuffer(file);
    return false; // Prevent default upload behavior
  };

  const handleRemoveProduct = useCallback((key) => {
    setDataSource((prev) => prev.filter((item) => item.key !== key));
  }, []);

  const columns = [
    {
      title: "Sl",
      dataIndex: "slno",
      key: "slno",
      width: 30,
      align: "center",
      fixed: "left",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 250,
      render: (text, record) => (
        <Input.TextArea
          autoSize={{ minRows: 1, maxRows: 6 }}
          bordered={false}
          value={record.name}
          maxLength={300}
          showCount={false}
          onChange={(e) =>
            handleChangeValueSet(e.target.value, record.key, "name")
          }
        />
      ),
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      width: 200,
      render: (_, record) => {
        const priceLength = String(record.price || '').length;
        const fontSize = priceLength > 8 ? '11px' : priceLength > 6 ? '12px' : '14px';
        return (
          <Input
            type="number"
            min={0}
            max={999999999999999}
            bordered={false}
            value={record.price}
            maxLength={15}
            style={{ 
              fontSize: fontSize,
              width: '100%',
              padding: '4px 8px'
            }}
            onKeyDown={(e) => {
              if (e.key === "e" || e.key === "E") {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              const value = e.target.value;
              if (value.length <= 15) {
                handleChangeValueSet(value, record.key, "price");
              }
            }}
          />
        );
      },
    },
    {
      title: "Discount",
      dataIndex: "discount",
      key: "discount",
      width: 200,
      render: (_, record) => {
        const discountLength = String(record.discount || '').length;
        const fontSize = discountLength > 8 ? '11px' : discountLength > 6 ? '12px' : '14px';
        return (
          <Input
            type="number"
            min={0}
            max={999999999999999}
            bordered={false}
            value={record.discount}
            maxLength={15}
            style={{ 
              fontSize: fontSize,
              width: '100%',
              padding: '4px 8px'
            }}
            onKeyDown={(e) => {
              if (e.key === "e" || e.key === "E") {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              const value = e.target.value;
              if (value.length <= 15) {
                handleChangeValueSet(value, record.key, "discount");
              }
            }}
          />
        );
      },
    },
    {
      title: "Quantity",
      width:80,
      dataIndex: "quantity",
      key: "quantity",
      render: (_, record) => (
        <Input
          type="number"
          min={0}
          max={9999999999}
          bordered={false}
          value={record.quantity}
          maxLength={10}
          onKeyDown={(e) => {
            if (e.key === "e" || e.key === "E") {
              e.preventDefault();
            }
          }}
          onChange={(e) => {
            const value = e.target.value;
            if (value.length <= 10) {
              handleChangeValueSet(value, record.key, "quantity");
            }
          }}
        />
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (_, record) => (
        <Input
          bordered={false}
          value={record.description}
          maxLength={200}
          onChange={(e) =>
            handleChangeValueSet(e.target.value, record.key, "description")
          }
        />
      ),
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      width: 120,
      render: (_, record) => {
        const itemTotal =
          Number(record.price) * Number(record.quantity) -
          Number(record.discount || 0);
        const totalValue = Number(itemTotal).toFixed(2);
        const totalLength = totalValue.length;
        const fontSize = totalLength > 10 ? '11px' : totalLength > 8 ? '12px' : '14px';
        return (
          <span style={{ 
            fontSize: fontSize,
            display: 'inline-block',
            width: '100%',
            textAlign: 'right'
          }}>
            {totalValue}
          </span>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (_, record) => (
        <Button
          danger
          type="link"
          onClick={() => handleRemoveProduct(record.key)}
        >
          Remove
        </Button>
      ),
    },
  ];
  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = File;
    link.download = "PURCHASE_EXCELL_FILE.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const resetFormAndData = () => {
    form.resetFields();
    setDataSource([]);
  };
  return (
    <Modal
        title={selectedPurchase?.id ? "Update Purchase" : "Add Purchase"}
        open={props.visible}
        onCancel={props.onClose}
        footer={false}
        width={modalWidth}
        // style={{ top: 20 }}
        // bodyStyle={{ maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' }}
      >
      <Form
        form={form}
        onFinish={handleFinish}
        initialValues={{
          supplierId: selectedPurchase?.supplierDetails?.account_no,
          purchase_date: selectedPurchase?.payment_date
            ? moment(selectedPurchase.payment_date)
            : null,
          description: selectedPurchase?.description,
        }}
      >
        <Row>
          <Col md={12}>
            <Form.Item
              label="Supplier"
              name="supplierId"
              rules={[{ required: true }]}
              labelCol={{ span: 7 }}
            >
              <Select
                size="medium"
                showSearch
                allowClear
                filterOption={(input, option) => {
                  const searchText = input.toLowerCase();
                  const optionText = option.children.toString().toLowerCase();
                  return optionText.includes(searchText);
                }}
                disabled={Boolean(selectedPurchase?.id)}
              >
                {props.accounts?.map((item) => (
                  <Select.Option key={item.account_no} value={item.account_no}>
                    {item.account_no} - {item.account_name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Purchase Date"
              name="purchase_date"
              rules={[{ required: true }]}
              labelCol={{ span: 7 }}
            >
              <DatePicker
                size="medium"
                format={dateFormat}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col md={12}>
            <Form.Item
              label="Description"
              name="description"
              labelCol={{ span: 7 }}
              rules={[{ required: true, message: "Description is required" }]}
            >
              <Input.TextArea rows={1} size="medium" />
            </Form.Item>
            <Form.Item
              label="Upload Excel"
              labelCol={{ span: 7 }}
              className="g-3"
            >
              <Upload
                beforeUpload={handleExcelUpload}
                accept=".xlsx,.xls"
                showUploadList={false}
                loading={uploading}
              >
                <Button loading={uploading} icon={<MdUpload />}>
                  {uploading ? "Uploading..." : "Upload Excel"}
                </Button>
              </Upload>
              &nbsp;&nbsp;&nbsp;
              <Button onClick={handleDownload} icon={<MdFileDownload />}>
                Sample File
              </Button>
            </Form.Item>
          </Col>
          <Col md={8} />
        </Row>
      </Form>
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        summary={renderSummary}
        // scroll={{ x: 'max-content', y: window.innerHeight < 768 ? 300 : undefined }}
        // size="small"
      />
      <Row justify="end" gutter={[16, 0]} style={{ marginTop: 16 }}>
        <Col>
          <Button
            onClick={() => {
              resetFormAndData();
              props.onClose();
            }}
          >
            Cancel
          </Button>
        </Col>
        <Col>
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={loading}
          >
            {selectedPurchase?.id ? "Update" : "Save"}
          </Button>
        </Col>
      </Row>
    </Modal>
  );
};

export default PurchaseForm;
